import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getItem, LocalLanguage } from '../../utils/localStorage';
import AuthAxios from '../../axios';
import { useNavigate } from 'react-router-dom';
import SjMainImage from '../../assets/images/er.png';
import SleepingJellyCouponModal, { SleepingJellyCouponModalType } from '../../components/sleepingJelly/CouponModal';
import SimpleLoading from '../../components/SimpleLoading';
import PhoneNumberModal from '../../components/sleepingJelly/PhoneNumberModal';
import { AuthStateContext } from '../../contexts/AuthContext';
import { sjLocalStorageHandler } from '../../utils/sleepingJelly';

/** Figma 7546:2745 — 2-4-4-4 세그먼트 */
const COUPON_SEGMENT_LENGTHS = [2, 4, 4, 4] as const;
const COUPON_SEGMENT_WIDTHS = [23, 71, 71, 71] as const;

function sanitizeCouponChars(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

function splitCouponIntoSegments(raw: string): string[] {
  const cleaned = sanitizeCouponChars(raw);
  const next = COUPON_SEGMENT_LENGTHS.map(() => '');
  let cursor = 0;
  for (let i = 0; i < COUPON_SEGMENT_LENGTHS.length; i += 1) {
    const len = COUPON_SEGMENT_LENGTHS[i];
    next[i] = cleaned.slice(cursor, cursor + len);
    cursor += len;
  }
  return next;
}

export default function SleepingJellyCouponPage() {
  const navigate = useNavigate();
  const [segments, setSegments] = useState<string[]>(() => COUPON_SEGMENT_LENGTHS.map(() => ''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setModal] = useState<boolean>(false);
  const [langauge, setLanguage] = useState('');

  /** 기존 API는 단일 input 값을 그대로 전송 — 대시 자동삽입 전이므로 대시 없이 전송 */
  const code = useMemo(() => segments.join(''), [segments]);

  useEffect(() => {
    const lan = getItem(LocalLanguage);
    setLanguage(lan);
  }, []);
  const [modalInfo, setModalInfo] = useState<SleepingJellyCouponModalType>({
    isSuccess: false,
    title: '',
    message: '',
    code: '',
  });
  const [productInfo, setProductInfo] = useState<any>(null);
  const { isAuthentication, setLogout } = useContext(AuthStateContext);

  useEffect(() => {
    const getCouponInfo = async () => {
      setLoading(true);
      setLogout(); // 슬리핑젤리 유저는 일단 무조건 로그아웃한 상태로 시작
      const result = (await AuthAxios.get('/sleeping-jelly/coupons/product')).data;
      if (result) {
        setProductInfo(result);
        setLoading(false);
      }
    };
    getCouponInfo();
  }, []);

  const focusSegment = (index: number) => {
    const el = inputRefs.current[index];
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  };

  const handleSegmentChange = (index: number, value: string) => {
    const cleaned = sanitizeCouponChars(value);
    const maxLen = COUPON_SEGMENT_LENGTHS[index];

    setSegments((prev) => {
      const next = [...prev];
      if (cleaned.length <= maxLen) {
        next[index] = cleaned;
        return next;
      }

      // 붙여넣기/초과 입력: 현재 칸부터 뒤 칸으로 분배
      let rest = cleaned;
      for (let i = index; i < COUPON_SEGMENT_LENGTHS.length && rest.length > 0; i += 1) {
        const take = COUPON_SEGMENT_LENGTHS[i];
        next[i] = rest.slice(0, take);
        rest = rest.slice(take);
      }
      return next;
    });

    if (cleaned.length >= maxLen && index < COUPON_SEGMENT_LENGTHS.length - 1) {
      requestAnimationFrame(() => focusSegment(index + 1));
    }
  };

  const handleSegmentKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void onSubmit();
      return;
    }
    if (e.key === 'Backspace' && !segments[index] && index > 0) {
      e.preventDefault();
      focusSegment(index - 1);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      const el = e.currentTarget;
      if (el.selectionStart === 0 && el.selectionEnd === 0) {
        e.preventDefault();
        focusSegment(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowRight' && index < COUPON_SEGMENT_LENGTHS.length - 1) {
      const el = e.currentTarget;
      if (el.selectionStart === el.value.length && el.selectionEnd === el.value.length) {
        e.preventDefault();
        focusSegment(index + 1);
      }
    }
  };

  const handleSegmentPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;

    const prefix = segments.slice(0, index).join('');
    const mergedRaw = sanitizeCouponChars(prefix + pasted);
    setSegments(splitCouponIntoSegments(mergedRaw));

    const filledLen = mergedRaw.length;
    let focusAt = COUPON_SEGMENT_LENGTHS.length - 1;
    let counted = 0;
    for (let i = 0; i < COUPON_SEGMENT_LENGTHS.length; i += 1) {
      counted += COUPON_SEGMENT_LENGTHS[i];
      if (filledLen < counted) {
        focusAt = i;
        break;
      }
      if (filledLen === counted) {
        focusAt = Math.min(i + 1, COUPON_SEGMENT_LENGTHS.length - 1);
        break;
      }
    }
    requestAnimationFrame(() => focusSegment(focusAt));
  };

  const onSubmit = async () => {
    if (code) {
      let resCode = '';
      let resSuccess = false;
      try {
        setLoading(true);
        console.log(' onSubmit ~ code:', code);
        await AuthAxios.post('/sleeping-jelly/coupons/validation', { code });
        sjLocalStorageHandler('SET', code);
        navigate('/auth/login/jelly');
      } catch (err: any) {
        console.error(' onSubmit ~ err:', err);
        resCode = err?.response?.data?.message;
        const responseInformation = (await AuthAxios.get(`/sleeping-jelly/coupons/response-code/${resCode}`)).data;
        setModalInfo({ ...responseInformation.resCodeInfo, isSuccess: resSuccess });
        setModal(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {loading && <SimpleLoading />}
      {openModal && <SleepingJellyCouponModal modalInfo={modalInfo} setModal={setModal} />}
      <div className="w-full h-full bg-[#13171E] flex flex-col items-center text-white pt-12 pb-4 overflow-auto">
        <div className="w-fit">
          <div className="flex justify-center items-center">
            <img src={SjMainImage} alt="사운드필 이용권 등록" className="object-none" />
          </div>
          <div className="px-5">
            <div className="mx-30 flex flex-col gap-5 py-8">
              <div className="flex w-full flex-col items-start">
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: '24px',
                    letterSpacing: -0.4,
                    color: '#FFFFFF',
                  }}
                >
                  이지레스트 구매자분들께 드리는
                </p>
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 24,
                    fontWeight: 600,
                    lineHeight: '34px',
                    letterSpacing: -0.6,
                    color: '#FFFFFF',
                  }}
                >
                  사운드필 이용권 등록 페이지입니다.
                </p>
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: -0.4,
                    color: '#B0B8C1',
                  }}
                >
                  의학적 근거 기반 웰니스 사운드를 만나보세요.
                </p>
              </div>
              <div
                role="group"
                aria-label="이용권 코드"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  width: '100%',
                  height: 56,
                  borderRadius: 8,
                  padding: 16,
                  border: '1px solid #333D4B',
                  background: '#191F28',
                  boxSizing: 'border-box',
                }}
              >
                {COUPON_SEGMENT_LENGTHS.map((maxLen, index) => (
                  <React.Fragment key={`coupon-seg-${index}`}>
                    <input
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={maxLen}
                      value={segments[index]}
                      onChange={(e) => handleSegmentChange(index, e.target.value)}
                      onKeyDown={(e) => handleSegmentKeyDown(index, e)}
                      onPaste={(e) => handleSegmentPaste(index, e)}
                      aria-label={`이용권 코드 ${index + 1}번째 칸`}
                      style={{
                        width: COUPON_SEGMENT_WIDTHS[index],
                        flexShrink: 0,
                        height: 24,
                        padding: 0,
                        margin: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: '#E5E8EB',
                        fontSize: 16,
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: -0.4,
                        textAlign: 'center',
                        caretColor: '#E5E8EB',
                      }}
                    />
                    {index < COUPON_SEGMENT_LENGTHS.length - 1 ? (
                      <span
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          color: '#E5E8EB',
                          fontSize: 16,
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: -0.4,
                        }}
                      >
                        -
                      </span>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
              <button
                type="submit"
                onClick={onSubmit}
                style={{
                  width: '100%',
                  height: 60,
                  borderRadius: 50,
                  background: '#4F68FC',
                }}
              >
                등록
              </button>
              <div style={{ color: '#6B7684', fontSize: 12, fontWeight: 500 }}>
                <div>사용 방법</div>
                <ul style={{ listStyleType: 'disc', paddingLeft: 15 }}>
                  <li>이용권 코드를 입력창에 정확히 입력 후 [등록] 버튼을 클릭하세요.</li>
                  <li>사운드필 앱을 열어 로그인하고 자유롭게 이용하세요. </li>
                </ul>
              </div>
              <div
                className="flex flex-col gap-[10px]"
                style={{ padding: '16px 20px', borderRadius: 10, background: '#191F28' }}
              >
                <h4 className="text-[16px]">📣 쿠폰이 여러 개 있으신가요?</h4>
                <div className="text-[14px]">
                  <p>여러 개의 쿠폰을 가지고 계시더라도</p>
                  <p>한 번에 동시에 사용하실 수 없으며,</p>
                  <p>나머지 쿠폰은 보관함에 최대 {productInfo?.SJ_COUPON_REGISTRATION_LIMIT}개 등록돼요.</p>
                  보관함에서 ‘사용하기'를 눌러 차례 차례 사용해 주세요.
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center">
              <div
                className="flex justify-center items-center text-[28px] font-bold"
                style={{
                  width: 206,
                  height: 72,
                }}
              >
                유의사항
              </div>
            </div>
            <ul className="mt-[20px] text-[14px]" style={{ listStyleType: 'disc', paddingLeft: 16, fontWeight: 400 }}>
              {langauge === 'en' ? (
                <>
                  <div className="mt-8">[General Terms]</div>
                  <li>Lost or incorrectly entered pass codes, or any user errors, cannot be reissued.</li>
                  <li>
                    Once registered, passes cannot be restored if the account is deleted or the app is uninstalled. Any
                    remaining validity period will be forfeited.
                  </li>
                  <li>
                    Passes are only supported on the latest version of the SoundPill app. Registration and certain
                    features may not function on older versions.
                  </li>
                  <li>
                    If unauthorized duplication, resale, or other fraudulent activities are detected, the pass will be
                    invalidated without prior notice, and legal action may be taken if necessary.
                  </li>
                  <li>
                    A “Easy rest Exclusive Pass” can be used together with a “SoundPill Pass.” However, multiple passes
                    of the same type cannot be used simultaneously, even if the codes differ.
                  </li>
                  <li>
                    If a pass of the same type is already active, additional passes will be stored in [My passes] (up to
                    15 passes).
                  </li>
                  <li>
                    Passes stored in [My passes] can be activated by tapping [Use] after the currently active pass of
                    the same type expires.
                  </li>
                  <li>
                    When registering a pass, if no pass of the same type is currently active, it will be activated
                    immediately.
                  </li>

                  <div className="mt-5">[Easy rest Exclusive Pass Terms]</div>
                  <li>This pass is a complimentary benefit provided with the purchase of an Easy rest product.</li>
                  <li>
                    The pass grants access to the “Easy rest Exclusive Session” content within the SoundPill app for 21
                    days.
                  </li>
                  <li>
                    The registration validity period of the Easy rest Exclusive Pass matches the product’s shelf life.
                    After this period, registration and use will no longer be available.
                  </li>
                  <li>
                    This pass is not an essential component of the product, is not sold separately, and cannot be
                    refunded, exchanged, or compensated.
                  </li>
                  <li>
                    Regardless of whether the pass is used, refunds or exchanges for Easy rest health supplements follow
                    the refund policy of the Easy rest product.
                  </li>
                  <li>
                    The pass can be used together with a SoundPill Pass. The content available under each pass is
                    applied separately.
                  </li>

                  <div className="mt-5">[SoundPill Pass Terms]</div>
                  <li>
                    This pass provides unlimited access to SoundPill app content (excluding Easy rest Exclusive Sessions
                    and related features) for a specified duration as a premium pass.
                  </li>
                  <li>The validity period of SoundPill Passes varies by product (e.g., 7 days, 30 days).</li>
                  <li>
                    Refunds for purchased SoundPill Passes will be processed in accordance with the Electronic Commerce
                    Act and our refund policy.
                  </li>
                </>
              ) : (
                <>
                  <div>이하 유의사항 전문</div>
                  <div className="mt-5">[공통 사항]</div>
                  <li>이용권 코드의 분실, 오입력 등 사용자 실수로 인한 경우 재발급되지 않습니다.</li>
                  <li>
                    등록된 이용권은 계정 탈퇴 또는 앱 삭제 시 복구되지 않으며, 남은 이용 기간이 소멸될 수 있습니다.
                  </li>
                  <li>
                    사운드필 앱의 최신 버전에서만 정상 작동하며, 구버전에서는 등록 또는 기능 사용이 제한될 수 있습니다.
                  </li>
                  <li>
                    무단 복제, 리셀, 비정상적인 사용 등 부정 유통이 확인될 경우, 사전 통보 없이 무효 처리되며, 필요 시
                    법적 조치가 취해질 수 있습니다.
                  </li>
                  <li>
                    ‘이지레스트 구매자 전용 이용권’과 ‘사운드필 이용권’은 동시 사용이 가능하며, 동일한 종류의
                    이용권끼리는 코드가 달라도 동시 사용이 불가합니다.
                  </li>
                  <li>동일한 종류의 이용권이 이미 사용 중일 경우, [이용권 보관함]에 15개까지 보관됩니다.</li>
                  <li>
                    [이용권 보관함]에 등록된 이용권들은 동일한 종류의 이용권 사용이 만료된 후, [사용] 버튼을 눌러 사용할
                    수 있습니다.
                  </li>
                  <li>이용권 등록 시, 동일한 종류의 사용 중인 이용권이 없다면, 등록 즉시 사용됩니다.</li>
                  <div className="mt-5">[‘이지레스트 구매자 전용 이용권’ 유의사항]</div>
                  <li>본 이용권은 이지레스트 제품 구매 시 무상 제공되는 사은품 혜택입니다.</li>
                  <li>해당 이용권은 사운드필 앱 내 ‘이지레스트 전용 세션 콘텐츠’에 한해 21일간 사용 가능합니다.</li>
                  <li>
                    ‘이지레스트 구매자 전용 이용권’의 등록 유효 기간은 제품의 유통기한과 동일하며, 기간 경과 시 등록 및
                    사용이 불가합니다.
                  </li>
                  <li>
                    ‘이지레스트 구매자 전용 이용권’은 제품 구성의 필수 항목이 아니며, 별도 판매되지 않는 사은품으로,
                    이용권 자체에 대한 환불·교환·보상은 제공되지 않습니다.
                  </li>
                  <li>
                    이용권 미사용 여부와 관계없이, ‘이지레스트 건강기능식품’ 자체의 환불·교환은 ‘이지레스트
                    건강기능식품’ 기준에 따라 처리됩니다.
                  </li>
                  <li>‘사운드필 이용권’과 동시 사용이 가능하며, 각 쿠폰에 따른 콘텐츠 범위는 별도로 적용됩니다.</li>
                  <div className="mt-5">[‘사운드필 이용권’ 유의사항]</div>
                  <li>
                    본 이용권은 사운드필 앱의 콘텐츠(이지레스트 구매자 전용 세션 기능 및 콘텐츠 제외)를 지정된 기간 동안
                    무제한 이용할 수 있는 프리미엄 이용권입니다.
                  </li>
                  <li>‘사운드필 이용권’은 7일, 30일 등 이용 기간이 상품별로 다릅니다.</li>
                  <li>
                    유료로 구매한 ‘사운드필 이용권’의 환불은 전자상거래법 및 당사 이용권 환불 정책에 따라 처리됩니다.
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
